/**
 * ч.1343: помилки перевірки даних (FastAPI 422, detail — список
 * {type, loc, msg, ctx}) людською мовою.
 *
 * Раніше ~40 редакторів клали цей список у стан і малювали `{error}` —
 * React падав («Objects are not valid as a React child»), сторінка
 * показувала «сталась непередбачена помилка». Перехоплювач axios
 * (api/client.ts) тепер замінює список на рядок з цієї функції ДО того,
 * як помилка дійде до сторінки; сам список лишається в `detail_items`.
 */

export interface ValidationItem {
  type?: string;
  loc?: unknown[];
  msg?: string;
  ctx?: Record<string, unknown>;
}

/** Назви полів, які найчастіше трапляються у формах (ключ — поле API). */
const FIELD_UK: Record<string, string> = {
  organization_id: 'Організація',
  document_number: 'Номер',
  document_date: 'Дата',
  contractor_id: 'Контрагент',
  contract_id: 'Договір',
  warehouse_id: 'Склад',
  warehouse_from_id: 'Зі складу',
  warehouse_to_id: 'На склад',
  nomenclature_id: 'Номенклатура',
  employee_id: 'Працівник',
  individual_id: 'Фізособа',
  department_id: 'Підрозділ',
  position_id: 'Посада',
  cash_register_id: 'Каса',
  bank_account_id: 'Рахунок',
  vat_rate_id: 'Ставка ПДВ',
  amount: 'Сума',
  total_amount: 'Сума',
  amount_without_vat: 'Сума без ПДВ',
  vat_amount: 'ПДВ',
  quantity: 'Кількість',
  price: 'Ціна',
  price_per_unit: 'Ціна',
  cost_amount: 'Собівартість',
  confirmed_amount: 'Підтверджено',
  expected_amount: 'Очікувана сума',
  advance_amount: 'Сума авансу',
  salary_amount: 'Оклад',
  account_code: 'Рахунок',
  date_from: 'Дата з',
  date_to: 'Дата по',
  period_start: 'Початок періоду',
  period_end: 'Кінець періоду',
  name: 'Назва',
  code: 'Код',
  comment: 'Коментар',
  adjustment_reason: 'Причина',
  reason: 'Причина',
  inn: 'РНОКПП',
  edrpou: 'ЄДРПОУ',
  iban: 'IBAN',
  items: 'Рядки',
  entries: 'Проводки',
};

function fieldLabel(loc: unknown[] | undefined): string {
  if (!Array.isArray(loc)) return '';
  const path = loc.filter((p) => p !== 'body' && p !== 'query' && p !== 'path');
  const parts: string[] = [];
  for (let i = 0; i < path.length; i++) {
    const p = path[i];
    if (typeof p === 'number') {
      parts.push(`рядок ${p + 1}`);
    } else if (typeof p === 'string' && (i === path.length - 1 || typeof path[i + 1] !== 'number')) {
      parts.push(FIELD_UK[p] ?? p);
    }
  }
  if (parts.length === 0) return '';
  const s = parts.join(', ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function messageUk(it: ValidationItem): string {
  const ctx = it.ctx ?? {};
  switch (it.type) {
    case 'missing': return 'обовʼязкове поле';
    case 'greater_than': return `має бути більше ${ctx.gt}`;
    case 'greater_than_equal': return `має бути не менше ${ctx.ge}`;
    case 'less_than': return `має бути менше ${ctx.lt}`;
    case 'less_than_equal': return `має бути не більше ${ctx.le}`;
    case 'string_too_short': return `закоротке (щонайменше ${ctx.min_length} симв.)`;
    case 'string_too_long': return `задовге (не більше ${ctx.max_length} симв.)`;
    case 'too_short': return 'порожньо — додайте хоча б один';
    case 'uuid_parsing':
    case 'uuid_type': return 'оберіть значення зі списку';
    case 'decimal_parsing':
    case 'float_parsing':
    case 'int_parsing':
    case 'decimal_type':
    case 'float_type':
    case 'int_type': return 'має бути числом';
    case 'date_parsing':
    case 'date_from_datetime_parsing':
    case 'datetime_parsing':
    case 'datetime_from_date_parsing':
    case 'date_type':
    case 'datetime_type': return 'неправильна дата';
    case 'literal_error':
    case 'enum': return 'недопустиме значення';
    case 'extra_forbidden': return 'зайве поле — оновіть сторінку';
    case 'string_pattern_mismatch': return 'неправильний формат';
    default: break;
  }
  const msg = it.msg ?? '';
  // Власні перевірки сервера (ValueError) — текст уже наш: прибираємо префікс Pydantic.
  return msg.replace(/^(Value error|Assertion failed), /, '') || 'неправильне значення';
}

/** Список помилок перевірки → один рядок (перші `limit`, далі «…і ще N»). */
export function humanizeValidationDetail(detail: unknown, limit = 3): string {
  if (typeof detail === 'string') return detail;
  if (!Array.isArray(detail) || detail.length === 0) return 'Невірні дані. Перевірте форму.';
  const lines = (detail as ValidationItem[]).slice(0, limit).map((it) => {
    const field = fieldLabel(it.loc);
    const msg = messageUk(it);
    return field ? `${field}: ${msg}` : msg;
  });
  let out = lines.join('; ');
  if (detail.length > limit) out += `; …і ще ${detail.length - limit}`;
  return out;
}
